import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

const LOGO_URL = "/logos/official/infocityjobs.png";

const FOOTER_PORTALS = [
  { key: "prop",   name: "Prop",   url: "/prop"   },
  { key: "jobs",   name: "Jobs",   url: "/",       active: true },
  { key: "cars",   name: "Cars",   url: "/cars"   },
  { key: "shop",   name: "Shop",   url: "/shop"   },
  { key: "boat",   name: "Boat",   url: "/boat"   },
  { key: "travel", name: "Travel", url: "/travel" },
];

export default function Footer() {
  const { t } = useLanguage();
  const { user } = useAuth();
  // Candidates get an app-like mobile shell (bottom nav) — the footer
  // is replaced by it on mobile; desktop footer is untouched.
  const hideOnMobile = user?.role === "candidate";

  return (
    <footer className={`bg-slate-900 text-slate-300 ${hideOnMobile ? "hidden lg:block" : ""}`} data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="mb-4" style={{ display: "flex", alignItems: "center", height: "58px", aspectRatio: "859 / 218", position: "relative", overflow: "hidden" }} data-testid="footer-logo">
              <img
                src={LOGO_URL}
                alt="InfoCityJobs"
                style={{
                  position: "absolute",
                  top: "-22.94%",
                  left: "-3.03%",
                  height: "146.79%",
                  width: "139.7%",
                  maxWidth: "none",
                  display: "block",
                }}
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-5">
              {t("footer.description")}
            </p>
            {/* Ecosystem pills — no emojis, clean text labels */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
                {t("footer.network")}
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="ecosystem-links">
                {FOOTER_PORTALS.map(({ key, name, url, active }) => (
                  <Link
                    key={key}
                    to={url}
                    data-testid={`ecosystem-${key}`}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                      active
                        ? "border-[#028CF8]/50 bg-[#028CF8]/10 text-[#028CF8]"
                        : "border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* For Job Seekers */}
          <div>
            <h4 className="font-poppins font-semibold text-white mb-4 text-sm tracking-wide">
              {t("footer.seekersTitle")}
            </h4>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: t("footer.browseJobs"), to: "/jobs" },
                { label: t("footer.companies"), to: "/companies" },
                { label: t("footer.savedJobs"), to: "/dashboard" },
                { label: t("footer.myApplications"), to: "/dashboard" },
              ].map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} className="text-slate-400 hover:text-white transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* For Employers */}
          <div>
            <h4 className="font-poppins font-semibold text-white mb-4 text-sm tracking-wide">
              {t("footer.employersTitle")}
            </h4>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: t("footer.postJob"), to: "/company/dashboard" },
                { label: t("footer.employerDash"), to: "/company/dashboard" },
                { label: t("footer.hiringSolutions"), to: "/company/dashboard" },
                { label: t("footer.applicantTracking"), to: "/company/dashboard" },
              ].map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} className="text-slate-400 hover:text-white transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-poppins font-semibold text-white mb-4 text-sm tracking-wide">
              {t("footer.companyTitle")}
            </h4>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: t("footer.aboutInfoCity"), to: "/" },
                { label: t("footer.privacy"), to: "/privacy" },
                { label: t("footer.terms"), to: "/terms" },
                { label: t("footer.contact"), to: "/contact" },
              ].map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} data-testid={`footer-link-${label}`} className="text-slate-400 hover:text-white transition-colors">{label}</Link>
                </li>
              ))}
              <li>
                <button type="button" data-testid="footer-cookie-preferences"
                  onClick={() => window.dispatchEvent(new CustomEvent("open-cookie-preferences"))}
                  className="text-slate-400 hover:text-white transition-colors">
                  {t("footer.cookiePrefs")}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-slate-500 text-xs">
            © {new Date().getFullYear()} {t("footer.copyright")}
          </p>
          <p className="text-slate-600 text-xs">
            {t("footer.partOf")}{" "}
            <span className="font-semibold text-[#028CF8]">InfoCity</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
